import { handleResponse } from "../utils/utils";
import { isHaddockHostname } from "../utils/haddockDomain";
import { invalidateZendeskMessengerSession } from "utils/zendeskAuth";
import { companyService, timeTypesService } from "services";

export const authService = {
	login,
	register,
	getToken,
	getCurrent,
	signup,
	logout,
	cleanStorage,
	setToken,
	setUser,
	checkCookieAndSetToken,
	initializeAuthService,
	getTimeRegistrationToken,
	getWebTerminalToken,
	// Nuevas funciones para manejo de empresas
	setCompanyTokens,
	switchCompany,
	getCurrentCompanyToken,
	getAllCompanyTokens,
	refresh,
	syncTokenFromStorage,
	getJwtEmployeeId,
};

try {
	authService.currentUser = JSON.parse(localStorage.getItem("user"));
	authService.token = JSON.parse(localStorage.getItem("token"));
	authService.isFromHaddock = false;
	// Nuevas propiedades para manejo de empresas
	authService.companyTokens =
		JSON.parse(localStorage.getItem("companyTokens")) || [];
	authService.currentCompanyId =
		localStorage.getItem("currentCompanyId") || null;
	const domain = window.location.hostname;
	authService.isHaddockDomain = isHaddockHostname(domain);

	// SSO Haddock: dominio HR o cookie de handoff desde Haddock Business
	if (authService.isHaddockDomain || getCookie("__plain_session")) {
		authService.isFromHaddock = true;
	}
} catch (e) {
	console.error("Error inicializando el authService", e);
}

function decodeJwtPayload(accessToken) {
	if (!accessToken || typeof accessToken !== "string") return null;
	try {
		const payloadPart = accessToken.split(".")[1];
		if (!payloadPart) return null;
		let base64 = payloadPart.replace(/-/g, "+").replace(/_/g, "/");
		const pad = base64.length % 4;
		if (pad) base64 += "=".repeat(4 - pad);
		return JSON.parse(atob(base64));
	} catch (_) {
		return null;
	}
}

function getJwtSubject(accessToken) {
	const payload = decodeJwtPayload(accessToken);
	const subject = payload?.sub;
	return typeof subject === "string" ? subject : null;
}

function hasValidHaddockStoredSession(currentUser, token) {
	return !!(currentUser && token?.access_token);
}

// Tras consumir la cookie SSO, la sesión vive en localStorage (F5, navegación interna).
function shouldKeepHaddockSessionWithoutCookie(isHaddockDomain, currentUser, token) {
	return isHaddockDomain && hasValidHaddockStoredSession(currentUser, token);
}

// La cookie __plain_session es un handoff de un solo uso; no debe persistir en hr.haddock.app.
function consumeHaddockSsoCookie() {
	if (getCookie("__plain_session")) {
		deleteCookie("__plain_session");
	}
}

// SSO Haddock: cookie nueva de otro usuario (impersonación A→B) vs multivenue (mismo email).
function isHaddockImpersonationSession(cookieToken, currentUser, activeToken) {
	if (!cookieToken || !activeToken?.access_token) return false;
	if (cookieToken === activeToken.access_token) return false;

	const cookieSubject = getJwtSubject(cookieToken);
	if (!cookieSubject) return false;

	const storedSubject =
		currentUser?.email || getJwtSubject(activeToken.access_token);
	if (!storedSubject) return true;

	return cookieSubject.toLowerCase() !== storedSubject.toLowerCase();
}

function isCookiePartOfCurrentHaddockSession(cookieToken, companyTokens) {
	if (!cookieToken || !companyTokens?.length) return false;
	return companyTokens.some((company) => company.access_token === cookieToken);
}

// Cookie distinta al token activo y ajena a la sesión guardada → nuevo handoff SSO.
function isNewHaddockSsoHandoff(cookieToken, activeToken, companyTokens) {
	if (!cookieToken || !activeToken?.access_token) return false;
	if (cookieToken === activeToken.access_token) return false;
	if (isCookiePartOfCurrentHaddockSession(cookieToken, companyTokens)) {
		return false;
	}
	if (
		isHaddockImpersonationSession(
			cookieToken,
			authService.currentUser,
			activeToken
		)
	) {
		return true;
	}
	return true;
}

function hasExplicitVenueSelection() {
	return !!(
		authService.currentCompanyId &&
		authService.companyTokens?.some(
			(c) => c.company_name === authService.currentCompanyId
		)
	);
}

// Restaura el JWT del venue activo desde companyTokens (sin recargar la página).
function restoreActiveVenueToken() {
	if (!hasExplicitVenueSelection()) {
		return false;
	}

	const companyName = authService.currentCompanyId;
	const companyToken = authService.companyTokens.find(
		(company) => company.company_name === companyName
	);

	if (!companyToken) {
		return false;
	}

	const newToken = {
		access_token: companyToken.access_token,
		token_type: companyToken.token_type,
		expires_in: companyToken.expires_in,
		company_name: companyName,
		other_company_tokens: authService.companyTokens
			.filter((company) => company.company_name !== companyName)
			.map((company) => ({
				access_token: company.access_token,
				token_type: company.token_type,
				expires_in: company.expires_in,
				company_name: company.company_name,
			})),
		ok: true,
	};

	authService.token = newToken;
	localStorage.setItem("token", JSON.stringify(newToken));
	return true;
}

async function initializeAuthService() {
	syncTokenFromStorage();

	// Si estamos en proceso de cambio de empresa, no hacer refresh ni limpiar tokens
	const isSwitchingCompany = sessionStorage.getItem("switching_company");
	if (isSwitchingCompany) {
		sessionStorage.removeItem("switching_company");
		return authService.currentUser || null;
	}

	if (authService.isFromHaddock) {
		const cookieToken = getCookie("__plain_session");

		// Sin cookie: mantener sesión en localStorage (ya consumida) o limpiar si no hay sesión.
		if (!cookieToken) {
			if (
				shouldKeepHaddockSessionWithoutCookie(
					authService.isHaddockDomain,
					authService.currentUser,
					authService.token
				)
			) {
				return authService.currentUser;
			}
			await cleanStorage();
			return;
		}

		if (
			isNewHaddockSsoHandoff(
				cookieToken,
				authService.token,
				authService.companyTokens
			)
		) {
			await cleanStorage();
			authService.isFromHaddock = true;
			const user = await checkCookieAndSetToken();
			return user;
		}

		const hasVenueSelection = hasExplicitVenueSelection();
		const cookieDiffersFromActiveToken =
			cookieToken &&
			authService.token?.access_token &&
			cookieToken !== authService.token.access_token;

		// Multivenue: la cookie SSO es el JWT del venue principal ya conocido.
		if (hasVenueSelection && cookieDiffersFromActiveToken) {
			restoreActiveVenueToken();
			consumeHaddockSsoCookie();
			return authService.currentUser;
		}

		const shouldRefreshTokens =
			!authService.currentUser ||
			!authService.token ||
			!authService.companyTokens?.length;

		if (shouldRefreshTokens) {
			const user = await checkCookieAndSetToken();
			return user;
		}

		if (authService.currentUser) {
			consumeHaddockSsoCookie();
		}
	}

	return authService.currentUser || null;
}

// get token from user
async function login(username, password) {
	if (authService.isFromHaddock) {
		// si es haddock borra la cookie antes de hacer login
		deleteCookie("__plain_session");
	}

	deleteCookie("timeRegistrationToken");
	deleteCookie("timeRegistrationCompany");

	const token = await getToken(username, password);

	if (token.ok) {
		// limpiar avatarsDB en indexedDB, que es el que almacena los avatares
		await clearIndexedDB();

		setToken(token);

		const user = await getCurrent();
		// console.log(user);
		if (user === undefined) {
			return {};
		} else if (user.ok) {
			await setUser(user);

			return {
				ok: true,
				user,
				token,
			};
		} else {
			return { errors: user.errors, ok: false };
		}
	} else {
		return { errors: token.errors, ok: false };
	}
}

// new user (and company) registration + login

async function register(user) {
	// Verifica si es un usuario de Haddock, en cuyo caso no aplica el registro
	if (authService.isFromHaddock) {
		return {
			ok: false,
			error_message: "El registro no aplica para usuarios de Haddock",
		};
	}

	const newUser = await signup(user);
	if (newUser.ok) {
		const token = await getToken(user.email, user.password);
		// console.log(token);

		if (token.ok) {
			setToken(token);
			await setUser(newUser);

			return {
				ok: true,
				user: newUser,
				token,
			};
		} else {
			return { errors: token.errors, ok: false };
		}
	} else {
		return { errors: newUser.errors, ok: false };
	}
}

// get token from user
function getToken(username, password) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${global.API_KEY}`,
			"Content-Type": "application/json",
			"X-Geo-IP": global.GetIPHeader,
		},
		body: JSON.stringify({
			grant_type: "password",
			username,
			password,
		}),
	};

	return fetch(`${global.BASE_API}auth/token/`, requestOptions).then(
		handleResponse
	);
}

// gets current user
function getCurrent() {
	if (!authService.token) {
		return Promise.resolve({});
	}

	const requestOptions = {
		method: "GET",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Content-Type": "application/json",
		},
	};

	return fetch(`${global.BASE_API}employees/current/`, requestOptions).then(
		handleResponse
	);
}

// new employee signup
function signup(user) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${global.API_KEY}`,
			"Content-Type": "application/json",
		},
		body: JSON.stringify(user),
	};

	return fetch(`${global.BASE_API}apps/signup/`, requestOptions).then(
		handleResponse
	);
}

// remove token & user from local storage to log user out
function logout(redirectUrl = "/login") {
	invalidateZendeskMessengerSession("app_logout");

	// Desconectar WebSocket global antes de limpiar el storage
	try {
		const { globalSocketService } = require("./globalSocketService");
		globalSocketService.disconnect();
	} catch (error) {
		console.error("Error al desconectar WebSocket en logout:", error);
	}

	// Si el token viene de Haddock, verifica si la cookie ha cambiado
	if (authService.isFromHaddock || authService.isHaddockDomain) {
		console.log("borrando cookie");
		deleteCookie("__plain_session");
	}

	cleanStorage();

	// Redirige si se proporciona una URL; de lo contrario, usa la predeterminada
	if (redirectUrl) {
		window.location.href = redirectUrl;
	}
}

// Función para limpiar IndexedDB
function clearIndexedDB() {
	return new Promise((resolve, reject) => {
		const request = indexedDB.deleteDatabase("avatarsDB");

		request.onsuccess = () => {
			resolve();
		};

		request.onerror = (event) => {
			reject(event.target.errorCode);
		};
	});
}

// Prefijos de filtros/selecciones por usuario que persisten en localStorage.
// Al hacer logout deben borrarse para no arrastrar empleados/grupos/fechas del
// usuario anterior en dispositivos compartidos. Para añadir nuevos filtros
// persistentes, basta con incluir aquí su prefijo.
const PERSISTENT_FILTER_PREFIXES = ["times_", "attendance_"];

function cleanPersistentFilters() {
	Object.keys(localStorage)
		.filter((key) =>
			PERSISTENT_FILTER_PREFIXES.some((prefix) => key.startsWith(prefix))
		)
		.forEach((key) => localStorage.removeItem(key));
}

async function cleanStorage() {
	invalidateZendeskMessengerSession("app_logout");

	// console.log("cleanStorage");
	// Limpiar solo tokens de autenticación y empresas
	localStorage.removeItem("token");
	localStorage.removeItem("user");
	localStorage.removeItem("companyTokens");
	localStorage.removeItem("currentCompanyId");

	// Limpiar filtros persistentes (fichajes, control de presencia, ...)
	cleanPersistentFilters();

	authService.token = null;
	authService.currentUser = null;
	authService.companyTokens = [];
	authService.currentCompanyId = null;

	// limpiar avatarsDB en indexedDB, que es el que almacena los avatares
	await clearIndexedDB();
}

function setToken(token, isFromHaddock = false) {
	// console.log("setToken");
	if (token) {
		localStorage.setItem("token", JSON.stringify(token));
		authService.token = token;
		authService.isFromHaddock = isFromHaddock;

		// Si el token tiene información de empresas, procesarla
		if (token.company_name) {
			setCompanyTokens(token);
		}
	} else {
		authService.token = null;
		authService.isFromHaddock = false;
		// Limpiar tokens de empresas
		authService.companyTokens = [];
		authService.currentCompanyId = null;
		localStorage.removeItem("companyTokens");
		localStorage.removeItem("currentCompanyId");
	}
}

async function setUser(user) {
	// Guardamos primero el usuario (para no perderlo si el token cae a null por un 401)
	localStorage.setItem("user", JSON.stringify(user));
	authService.currentUser = user;

	if (!authService.token) return;

	// Suscripción (puede disparar 401 y limpiar token)
	try {
		if (user.company) {
			var subscription = await companyService.getSubscriptions(user.company.id);
			// console.log(subscription);
			if (subscription && subscription.ok) {
				user.subscription = subscription[0];

				// vuelve a persistir por si se actualizó
				localStorage.setItem("user", JSON.stringify(user));
				authService.currentUser = user;
			}
		}
	} catch (e) {
		console.warn("Error obteniendo suscripción:", e);
	}

	// Si en la llamada anterior se perdió el token (401→logout), cortamos aquí
	if (!authService.token) return;

	try {
		var employee_with_time_types = await timeTypesService.getContractTimeTypes({
			employee_id: user.id,
		});
		user.employee_with_time_types = employee_with_time_types.ok
			? employee_with_time_types
			: [];

		localStorage.setItem("user", JSON.stringify(user));
		authService.currentUser = user;
	} catch (e) {
		console.warn("Error obteniendo time types:", e);
		user.employee_with_time_types = [];
	}
}

function getCookie(name) {
	const value = `; ${document.cookie}`;
	const parts = value.split(`; ${name}=`);
	if (parts.length === 2) return parts.pop().split(";").shift();
}

function deleteCookie(name) {
	// Intenta borrar la cookie para el dominio actual (sin especificar domain)
	document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

	// Intenta borrar la cookie para el dominio genérico ".haddock.app"
	const currentDomain = window.location.hostname;
	if (currentDomain.endsWith(".haddock.app")) {
		console.log("borrando cookie para .haddock.app");
		document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.haddock.app`;
	}
}

// Relee token/empresa desde localStorage (p. ej. cambio de venue en otra pestaña).
function syncTokenFromStorage() {
	try {
		const storedToken = JSON.parse(localStorage.getItem("token"));
		if (storedToken?.access_token) {
			authService.token = storedToken;
		}
		const storedCompanyTokens = JSON.parse(
			localStorage.getItem("companyTokens")
		);
		if (Array.isArray(storedCompanyTokens)) {
			authService.companyTokens = storedCompanyTokens;
		}
		const storedCompanyId = localStorage.getItem("currentCompanyId");
		if (storedCompanyId) {
			authService.currentCompanyId = storedCompanyId;
		}
	} catch (_) {
		/* noop */
	}
	return authService.token;
}

// employeeId del JWT: es el que el backend usa como "Logged on" en el WS.
function getJwtEmployeeId() {
	syncTokenFromStorage();
	const payload = decodeJwtPayload(authService.token?.access_token);
	if (!payload) return null;
	const id = payload.employeeId ?? payload.employee_id;
	if (id == null || id === "") return null;
	const numericId = Number(id);
	return Number.isFinite(numericId) ? numericId : null;
}

async function checkCookieAndSetToken() {
	const cookieToken = getCookie("__plain_session");
	if (!cookieToken) {
		console.warn("No hay cookie presente. No se intenta autenticar.");
		return null; // Detén el flujo si no hay cookie
	}

	syncTokenFromStorage();

	try {
		if (
			isNewHaddockSsoHandoff(
				cookieToken,
				authService.token,
				authService.companyTokens
			)
		) {
			await cleanStorage();
			authService.isFromHaddock = true;
		}

		// Con selección explícita de venue, usar su JWT en lugar de la cookie (venue principal SSO).
		if (
			hasExplicitVenueSelection() &&
			isCookiePartOfCurrentHaddockSession(
				cookieToken,
				authService.companyTokens
			)
		) {
			restoreActiveVenueToken();
			authService.isFromHaddock = true;
		} else {
			setToken({ access_token: cookieToken }, true);
		}

		const user = await getCurrent();
		if (user && user.ok) {
			await setUser(user);

			// Llamar a refresh para obtener todos los tokens de las empresas
			try {
				const refreshResult = await refresh();
				if (!refreshResult.ok) {
					console.warn(
						"No se pudieron obtener los tokens de empresas:",
						refreshResult.errors
					);
				}
			} catch (refreshError) {
				console.error("Error al hacer refresh de tokens:", refreshError);
				// No hacer logout, continuar con el token actual
			}

			consumeHaddockSsoCookie();
			return user;
		} else {
			console.warn(
				"El token es inválido o el usuario no es válido. Cerrando sesión."
			);
			authService.logout();
			return null;
		}
	} catch (error) {
		console.error("Error al obtener el usuario actual:", error);
		authService.logout();
		return null;
	}
}

function getTimeRegistrationToken() {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Content-Type": "application/json",
		},
	};

	return fetch(
		`${global.BASE_API}auth/time-registration-token/`,
		requestOptions
	).then(handleResponse);
}

// Obtiene el token del terminal de fichajes a partir del email/password
// configurados en la company. No requiere sesión previa de Plain, por eso usa
// la API_KEY como portador. Devuelve { access_token, token_type, expires_in }.
function getWebTerminalToken(username, password) {
	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${global.API_KEY}`,
			"Content-Type": "application/json",
			"X-Geo-IP": global.GetIPHeader,
		},
		body: JSON.stringify({
			grant_type: "password",
			username,
			password,
		}),
	};

	return fetch(`${global.BASE_API}auth/web-terminal/`, requestOptions).then(
		handleResponse
	);
}

// Nuevas funciones para manejo de empresas

// Procesar y guardar tokens de empresas
function setCompanyTokens(tokenResponse) {
	const allCompanies = [];

	// Añadir la empresa principal
	if (tokenResponse.company_name) {
		allCompanies.push({
			access_token: tokenResponse.access_token,
			token_type: tokenResponse.token_type,
			expires_in: tokenResponse.expires_in,
			company_name: tokenResponse.company_name,
			is_primary: true,
		});
	}

	// Añadir las otras empresas
	if (tokenResponse.other_company_tokens) {
		tokenResponse.other_company_tokens.forEach((companyToken) => {
			allCompanies.push({
				access_token: companyToken.access_token,
				token_type: companyToken.token_type,
				expires_in: companyToken.expires_in,
				company_name: companyToken.company_name,
				is_primary: false,
			});
		});
	}

	authService.companyTokens = allCompanies;
	localStorage.setItem("companyTokens", JSON.stringify(allCompanies));

	// Mantener la selección del usuario si sigue siendo válida; si no, usar la principal.
	if (authService.currentCompanyId) {
		const stillValid = allCompanies.some(
			(c) => c.company_name === authService.currentCompanyId
		);
		if (!stillValid) {
			console.warn(
				`[SESSION] currentCompanyId "${authService.currentCompanyId}" ya no está en companyTokens; usando venue principal`
			);
			authService.currentCompanyId = tokenResponse.company_name;
			localStorage.setItem("currentCompanyId", tokenResponse.company_name);
		}
	} else {
		authService.currentCompanyId = tokenResponse.company_name;
		localStorage.setItem("currentCompanyId", tokenResponse.company_name);
	}
}

// Cambiar empresa activa
function switchCompany(companyName) {
	const companyToken = authService.companyTokens.find(
		(company) => company.company_name === companyName
	);

	if (companyToken) {
		// Crear el token completo manteniendo la estructura original
		const newToken = {
			access_token: companyToken.access_token,
			token_type: companyToken.token_type,
			expires_in: companyToken.expires_in,
			company_name: companyName,
			other_company_tokens: authService.companyTokens
				.filter((company) => company.company_name !== companyName)
				.map((company) => ({
					access_token: company.access_token,
					token_type: company.token_type,
					expires_in: company.expires_in,
					company_name: company.company_name,
				})),
			ok: true,
		};

		// Actualizar el token principal
		authService.token = newToken;

		// Guardar el token actualizado en localStorage
		localStorage.setItem("token", JSON.stringify(newToken));

		// Actualizar la empresa actual
		authService.currentCompanyId = companyName;
		localStorage.setItem("currentCompanyId", companyName);

		// Marcar que estamos en proceso de cambio de empresa para evitar refresh automático
		sessionStorage.setItem("switching_company", "true");

		// Si estamos en una ruta ligada a IDs de venue, forzamos ir al inicio.
		const shouldRedirectToRoot = shouldRedirectToRootOnCompanySwitch(
			window.location.pathname
		);
		if (shouldRedirectToRoot) {
			console.log(
				`[SESSION] 🔄 Cambio a empresa ${companyName} desde ruta sensible (${window.location.pathname}). Redirigiendo a /`
			);
			window.location.assign("/");
		} else {
			// Recargar la página para que todos los componentes se inicialicen con la nueva empresa
			console.log(
				`[SESSION] 🔄 Forzando recarga de página por cambio a empresa: ${companyName}`
			);
			window.location.reload();
		}

		return true;
	}

	return false;
}

// Obtener token de la empresa actual
function getCurrentCompanyToken() {
	return authService.token;
}

// Obtener todas las empresas disponibles
function getAllCompanyTokens() {
	return authService.companyTokens;
}

// Refrescar tokens después de crear una empresa
async function refresh() {
	if (!authService.token) {
		throw new Error("No hay token disponible para hacer refresh");
	}

	const requestOptions = {
		method: "POST",
		headers: {
			Authorization: `Bearer ${authService.token.access_token}`,
			"Content-Type": "application/json",
		},
	};

	try {
		const response = await fetch(
			`${global.BASE_API}auth/refresh`,
			requestOptions
		);
		const data = await handleResponse(response);

		if (data.ok) {
			// Actualizar los tokens de empresas con la nueva respuesta
			setCompanyTokens(data);
			return { ok: true, data };
		} else {
			return { ok: false, errors: data.errors };
		}
	} catch (error) {
		console.error("Error refreshing tokens:", error);
		return { ok: false, error: error.message };
	}
}

function shouldRedirectToRootOnCompanySwitch(pathname) {
	if (!pathname || pathname === "/") {
		return false;
	}

	const normalizedPath = pathname.endsWith("/")
		? pathname.slice(0, -1)
		: pathname;

	const sensitiveRoutePatterns = [
		/^\/plans\/[^/]+\/[^/]+@[^/]+$/,
		/^\/documents\/[^/]+$/,
		/^\/employees\/[^/]+$/,
		/^\/groups\/[^/]+$/,
	];

	return sensitiveRoutePatterns.some((pattern) => pattern.test(normalizedPath));
}
